import { use, useState } from 'react';

import { type CompanyEmployeeDetailsDto } from '@/api/generated/models/company-employee-details-dto';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { DataOperationsErrorContext } from '@/errorHandling/DataOperationsErrorContext.ts';
import { useTypedParams } from '@/hooks/useTypedParams.hook.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { useGetUserCompanyData } from '@/views/userDetails/util/useGetUserCompanyData.util.ts';
import { useGetUserCompanyEmployeeData } from '@/views/userDetails/util/useGetUserCompanyEmployeeData.util.ts';

type Return = {
  employeeDetails: CompanyEmployeeDetailsDto | undefined;
  getEmployeeDetails: (myProfileView: boolean) => Promise<void>;
  isError: boolean;
  isGettingEmployeeDetails: boolean;
};

export const useGetEmployeeDetails = (): Return => {
  const {
    getUserCompanyData,
    isError: isErrorUserCompanyData,
    isGetting: isGettingUserCompanyData,
  } = useGetUserCompanyData();
  const {
    getUserCompanyEmployeeData,
    isError: isErrorGettingUserCompanyEmployeeData,
    isGetting: isGettingUserCompanyEmployeeData,
  } = useGetUserCompanyEmployeeData();
  const {
    user: { userId },
  } = useUserContext();
  const { companyId, employeeId } = useTypedParams(RouteEnum.COMPANY_EMPLOYEE);
  const { handleMinorError } = use(DataOperationsErrorContext);
  const [employeeDetails, setEmployeeDetails] = useState<CompanyEmployeeDetailsDto | undefined>();

  return {
    employeeDetails,
    getEmployeeDetails: async myProfileView => {
      if (myProfileView) {
        if (!userId) {
          handleMinorError('userId is not defined');
          throw new Error('userId is not defined');
        }
        const res = await getUserCompanyData({ userId });
        setEmployeeDetails(res);
        return;
      }
      if (!companyId || !employeeId) {
        handleMinorError('companyId or employeeId is not defined');
        throw new Error('companyId or employeeId is not defined');
      }
      const res = await getUserCompanyEmployeeData({ companyId, employeeId });
      setEmployeeDetails(res);
    },
    isError: isErrorGettingUserCompanyEmployeeData || isErrorUserCompanyData,
    isGettingEmployeeDetails: isGettingUserCompanyData || isGettingUserCompanyEmployeeData,
  };
};
